"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const bignumber_js_1 = require("bignumber.js");
const errors_1 = require("@ledgerhq/errors");
const currencies_1 = require("../../currencies");
const logic_1 = require("./logic");
const api_1 = require("./api");
const preload_1 = require("./preload");
const errors_2 = require("./errors");
const constants_1 = require("./constants");
const getTransactionStatus = (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    if (t.mode === "send") {
        return yield getSendTransactionStatus(a, t);
    }
    const errors = {};
    const warnings = {};
    const useAllAmount = !!t.useAllAmount;
    if (!t.fees) {
        errors.fees = new errors_1.FeeNotLoaded();
    }
    const estimatedFees = t.fees || new bignumber_js_1.BigNumber(0);
    const maxAmount = (0, logic_1.getMaxAmount)(a, t, estimatedFees);
    const maxAmountWithFees = (0, logic_1.getMaxAmount)(a, t);
    const spendableBalanceWithFees = (0, logic_1.getMaxAmount)(a, Object.assign(Object.assign({}, t), { mode: "stake" }));
    const stakingThreshold = (0, logic_1.getYoctoThreshold)();
    const totalSpent = (0, logic_1.getTotalSpent)(a, t, estimatedFees);
    const amount = useAllAmount ? maxAmount : new bignumber_js_1.BigNumber(t.amount);
    const isStakeAndNotEnoughBalance = t.mode === "stake" && (totalSpent.gt(maxAmountWithFees) || maxAmountWithFees.lt(estimatedFees));
    const isUnstakeOrWithdrawAndNotEnoughBalance = ["unstake", "withdraw"].includes(t.mode) &&
        (totalSpent.gt(spendableBalanceWithFees) || spendableBalanceWithFees.lt(estimatedFees));
    if (isStakeAndNotEnoughBalance || isUnstakeOrWithdrawAndNotEnoughBalance) {
        errors.amount = new errors_1.NotEnoughBalance();
    }
    else if (["stake", "unstake", "withdraw"].includes(t.mode) && amount.lt(stakingThreshold)) {
        const currency = (0, currencies_1.getCryptoCurrencyById)("near");
        const formattedStakingThreshold = (0, currencies_1.formatCurrencyUnit)(currency.units[0], stakingThreshold.plus(constants_1.YOCTO_THRESHOLD_VARIATION), {
            showCode: true,
        });
        errors.amount = new errors_2.NearStakingThresholdNotMet(undefined, {
            threshold: formattedStakingThreshold,
        });
    }
    else if (t.mode === "unstake" && amount.gt(maxAmount)) {
        errors.amount = new errors_2.NearNotEnoughStaked();
    }
    else if (t.mode === "withdraw" && amount.gt(maxAmount)) {
        errors.amount = new errors_2.NearNotEnoughAvailable();
    }
    else if (amount.lte(0) && !t.useAllAmount) {
        errors.amount = new errors_1.AmountRequired();
    }
    if (t.mode === "stake" && !errors.amount && t.useAllAmount) {
        warnings.amount = new errors_2.NearUseAllAmountStakeWarning();
    }
    return Promise.resolve({
        errors,
        warnings,
        estimatedFees,
        amount,
        totalSpent,
    });
});
const getSendTransactionStatus = (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const errors = {};
    const warnings = {};
    const useAllAmount = !!t.useAllAmount;
    const { storageCost } = (0, preload_1.getCurrentNearPreloadData)();
    const newAccountStorageCost = storageCost.multipliedBy(constants_1.NEW_ACCOUNT_SIZE);
    const currency = (0, currencies_1.getCryptoCurrencyById)("near");
    const formattedNewAccountStorageCost = (0, currencies_1.formatCurrencyUnit)(currency.units[0], newAccountStorageCost, {
        showCode: true,
    });
    let recipientIsNewAccount;
    if (!t.recipient) {
        errors.recipient = new errors_1.RecipientRequired();
    }
    else if (!(0, logic_1.isValidAddress)(t.recipient)) {
        errors.recipient = new errors_1.InvalidAddress();
    }
    else {
        const accountDetails = yield (0, api_1.fetchAccountDetails)(t.recipient);
        if (!accountDetails) {
            recipientIsNewAccount = true;
            if ((0, logic_1.isImplicitAccount)(t.recipient)) {
                warnings.recipient = new errors_2.NearNewAccountWarning(undefined, {
                    formattedNewAccountStorageCost,
                });
            }
            else {
                errors.recipient = new errors_2.NearNewNamedAccountError();
            }
        }
    }
    if (a.freshAddress === t.recipient) {
        warnings.recipient = new errors_1.InvalidAddressBecauseDestinationIsAlsoSource();
    }
    if (!t.fees) {
        errors.fees = new errors_1.FeeNotLoaded();
    }
    const estimatedFees = t.fees || new bignumber_js_1.BigNumber(0);
    const totalSpent = (0, logic_1.getTotalSpent)(a, t, estimatedFees);
    const maxAmount = (0, logic_1.getMaxAmount)(a, t, estimatedFees);
    const maxAmountWithFees = (0, logic_1.getMaxAmount)(a, t);
    const amount = useAllAmount ? maxAmount : new bignumber_js_1.BigNumber(t.amount);
    if (totalSpent.gt(maxAmountWithFees) || maxAmountWithFees.lt(estimatedFees)) {
        errors.amount = new errors_1.NotEnoughBalance();
    }
    else if (amount.lte(0) && !t.useAllAmount) {
        errors.amount = new errors_1.AmountRequired();
    }
    else if (recipientIsNewAccount && amount.lt(newAccountStorageCost)) {
        errors.amount = new errors_2.NearActivationFeeNotCovered(undefined, {
            formattedNewAccountStorageCost,
        });
    }
    if (((_a = a.nearResources) === null || _a === void 0 ? void 0 : _a.stakingPositions) &&
        a.nearResources.stakingPositions.length > 0 &&
        useAllAmount) {
        warnings.amount = new errors_2.NearRecommendUnstake();
    }
    return Promise.resolve({
        errors,
        warnings,
        estimatedFees,
        amount,
        totalSpent,
    });
});
exports.default = getTransactionStatus;
//# sourceMappingURL=js-getTransactionStatus.js.map